import {
  Checkbox,
  FormControl,
  ListItemText,
  ListSubheader,
  MenuItem,
  PaperProps,
  Select,
  SelectChangeEvent,
  checkboxClasses
} from '@mui/material';
import clsx from 'clsx';
import { InputField } from 'components/inputField';
import { Stack } from 'components/stack';
import { Tooltip } from 'components/tooltip';
import {
  ChangeEvent,
  ComponentProps,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
  FC
} from 'react';
import { Tag } from 'components/tag';

import style from './insurancePlanSelect.module.scss';

interface DropdownData {
  id: number | string;
  name: string;
  category?: string;
  info?: string;
  tooltipPlacement?: string;
  tooltipDelay?: number;
}

type Type = 'default' | 'success' | 'warning';

type Size = 'normal' | 'medium' | 'small';

function categorizeItems({
  array,
  categories,
  enableSorting
}: {
  array: DropdownData[];
  categories?: { id: string; label: string }[];
  enableSorting: boolean;
}) {
  const categorizedItems: {
    [key: string]: { id: string; name: string; children: DropdownData[] };
  } = {};
  const categorizedArray = [];

  array.forEach((item) => {
    if (item.category) {
      if (!categorizedItems[item.category]) {
        const found = categories?.find((category) => category.id === item.category);
        if (found) {
          categorizedItems[item.category] = {
            id: found.id,
            name: found?.label,
            children: []
          };
        }
      }
      categorizedItems[item.category].children?.push(item);
    } else {
      categorizedArray.push(item);
    }
  });

  const sortedCategorizedGroups = Object.values(categorizedItems).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  for (const group of sortedCategorizedGroups) {
    const sortedChildren = group.children;
    categorizedArray.push({ ...group, children: sortedChildren });
  }

  if (enableSorting) {
    categorizedArray.sort((a, b) => a.name.localeCompare(b.name));
  }

  return categorizedArray;
}

interface PropsTooltip {
  name: string;
  fitWidth?: boolean;
  hasValue: boolean;
}

const TruncatedText: FC<PropsTooltip> = ({ name, fitWidth, hasValue }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const { scrollWidth, offsetWidth } = textRef.current;
      setIsTextTruncated(scrollWidth > offsetWidth);
    }
  }, [name]);

  return (
    <div ref={textRef} style={{ width: hasValue ? 'calc(100% - 24px)' : '100%' }}>
      {isTextTruncated ? (
        <Tooltip
          toolTipText={isTextTruncated ? name : ''}
          disableInteractive
          blackTooltip
          placement="bottom-start"
          enterDelay={300}
        >
          <ListItemText
            primary={name}
            sx={{
              '& span': {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis'
              }
            }}
          />
        </Tooltip>
      ) : (
        <ListItemText primary={name} />
      )}
    </div>
  );
};

interface DropdownProps {
  ref?: string;
  key?: string;
  id?: string;
  name?: string;
  data: DropdownData[];
  label?: string;
  value: { id: string; name: string }[] | { id?: string; name?: string } | null;
  disabled?: boolean;
  rounded?: boolean;
  className?: string;
  sx?: ComponentProps<typeof Select>['sx'];
  type?: Type;
  size?: Size;
  onBlur?: ComponentProps<typeof Select>['onBlur'];
  onChange: (event: SelectChangeEvent<string[] | string>) => void;
  onFocus?: (event: ChangeEvent<HTMLSelectElement>) => void;
  categoryList?: { id: string; label: string }[];
  onInputBlur?: ComponentProps<typeof InputField>['onBlur'];
  onInputChange?: ComponentProps<typeof InputField>['onChange'];
  inputValue?: string;
  categorySelectable?: boolean;
  withGradient?: boolean;
  smallDropdown?: boolean;
  enableSorting?: boolean;
  fitWidth?: boolean;
  open?: boolean;
  getOpen?: (e: any) => void;
  fitWidthMore?: number;
  searchInputValue?: string;
  onSearchInputChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  showTitle: boolean;
  onClose?: () => void;
  multiple?: boolean;
  withFavorites?: boolean;
}
const InsurancePlanSelect: FunctionComponent<DropdownProps> = ({
  name,
  label,
  data,
  onBlur,
  onChange,
  value,
  disabled = false,
  rounded = false,
  className,
  type = 'default',
  size = 'normal',
  sx,
  categoryList,
  onInputBlur,
  categorySelectable = false,
  withGradient = false,
  smallDropdown = false,
  enableSorting = false,
  open = false,
  fitWidthMore = 0,
  searchInputValue,
  onSearchInputChange,
  showTitle,
  onClose,
  multiple = true
}) => {
  const [expanded, setExpanded] = useState<boolean>(open);
  const [autoFocus, setAutoFocus] = useState<boolean>(true);
  const [selectWidth, setSelectWidth] = useState<number>(0);
  const paperRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  const MenuProps: ComponentProps<typeof Select>['MenuProps'] = {
    PaperProps: {
      ref: paperRef,
      'data-test-id': 'dropdown-list',
      sx: {
        maxHeight: smallDropdown ? '300px' : '450px',
        width: selectWidth,
        marginLeft: fitWidthMore > 0 ? '18px' : 'auto',
        '& .MuiList-root': {
          padding: '0px',
          boxShadow: '0px 2px 16px rgba(80, 117, 177, 0.1)',
          borderRadius: '8px'
        },
        '& .MuiListSubheader-root': {
          padding: '15px',
          fontWeight: '500',
          fontSize: '16px',
          lineHeight: '140%',
          fontFamily: '"ProximaNova", sans-serif',
          color: '#314157',
          backgroundColor: '#F1F5F9'
        },
        '& .Mui-selected': {
          backgroundColor: '#EFF9FB !important',
          color: '#096F84'
        },
        '& .MuiMenuItem-root': {
          padding: '15px',
          backgroundColor: '#fff',
          '& .MuiTypography-root': {
            fontWeight: '500',
            fontSize: '16px',
            lineHeight: '22.4px',
            fontFamily: '"ProximaNova", sans-serif',
            color: '#314157'
          }
        },
        '& .MuiCheckbox-root': {
          padding: '0 11px 0 0'
        },
        '& .MuiMenuItem-root:hover': {
          backgroundColor: '#F1F5F9'
        }
      }
    } as Partial<PaperProps> & { 'data-test-id': string },
    autoFocus
  };

  useEffect(() => {
    if (expanded) {
      setAutoFocus(false);
    } else {
      setAutoFocus(true);
    }
  }, [expanded]);

  const hasValue = Array.isArray(value)
    ? value.length > 0
    : (value?.id?.toString()?.length ?? 0) > 0;

  const filteredData = data;

  const renderTag = (v: { id: string; name: string }) => {
    if (!Array.isArray(value)) {
      return null;
    }

    return (
      <Tag
        blueSkin
        key={v.id}
        onDelete={() =>
          onChange({
            target: { value: value.filter((k) => v.id !== k.id).map((k) => k.id), name }
          } as unknown as SelectChangeEvent<string[]>)
        }
        label={v.name}
      />
    );
  };

  const renderMenuitem = (item: DropdownData) => {
    return (
      <MenuItem key={item.id} value={item.id?.toString()}>
        {multiple && (
          <Checkbox
            checked={
              Array.isArray(value)
                ? value.some((v) => v.id.toString() === item.id?.toString())
                : value?.id === item.id?.toString()
            }
            sx={{
              [`&, &.${checkboxClasses.root}`]: {
                color: '#8DA0B9'
              },
              [`&, &.${checkboxClasses.checked}`]: {
                color: '#096F84'
              }
            }}
          />
        )}
        <Stack width="100%" display="flex" flexDirection="row" justifyContent="space-between">
          {item.info ? (
            <Tooltip
              placement={item.tooltipPlacement}
              toolTipText={item.info}
              disableInteractive
              enterDelay={item.tooltipDelay ?? 300}
              fullWidth
            >
              <ListItemText
                primary={item.name}
                sx={{
                  width: '100%',
                  '& span': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Tooltip>
          ) : (
            <TruncatedText
              name={item.name}
              fitWidth
              hasValue={hasValue && !multiple && value?.toString() === item.id?.toString()}
            />
          )}
          {hasValue && !multiple && value?.toString() === item.id?.toString() && (
            <div
              onClick={() =>
                onChange({
                  target: { value: '', name }
                } as unknown as SelectChangeEvent<string>)
              }
              className={clsx('material-icons-outlined', style.icon, style.iconClose)}
            >
              close
            </div>
          )}
        </Stack>
      </MenuItem>
    );
  };

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onSearchInputChange) {
      onSearchInputChange(event);
      return;
    }
  };

  const sortValues = (val: string[] | string, sortOrder: DropdownData[]): string[] | string => {
    let sortedValues: string[] | string = [];

    if (Array.isArray(val)) {
      sortedValues = sortOrder
        .filter((item) => val.includes(item.id.toString()))
        .map((item) => item.id.toString());
    } else {
      sortedValues = val;
    }

    return sortedValues;
  };

  const handleChange = (e: SelectChangeEvent<string | string[]>) => {
    onChange({
      ...e,
      target: {
        ...e.target,
        value: categoryList ? sortValues(e.target.value, filteredData) : e.target.value
      }
    } as unknown as SelectChangeEvent<string[]>);
  };

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      setSelectWidth(entry.contentBoxSize[0].inlineSize);
    }
  });

  useEffect(() => {
    setTimeout(() => {
      if (selectRef.current) {
        resizeObserver.observe(selectRef.current);
      }
    }, 0);

    return () => {
      if (selectRef.current) {
        resizeObserver.unobserve(selectRef.current);
      }
    };
  }, [expanded]);

  const categorizedItems = categorizeItems({
    array: filteredData,
    categories: categoryList,
    enableSorting
  });

  useEffect(() => {
    setExpanded(open);
  }, [open]);

  return (
    <div className={style.inputContainer}>
      <FormControl fullWidth>
        <Select
          ref={selectRef}
          open={expanded}
          onOpen={() => setExpanded(true)}
          onClose={() => {
            setExpanded(false);
            onClose?.();
          }}
          IconComponent={() => (
            <>
              <span
                data-expanded={expanded}
                className={clsx(
                  'material-icons',
                  style.icon,
                  disabled && style.iconDisabled,
                  size === 'small' && style.smallIcon,
                  type === 'success' && style.iconSuccess,
                  type === 'warning' && style.iconWarning
                )}
              >
                expand_more
              </span>
            </>
          )}
          name={name}
          value={Array.isArray(value) ? value.map((item) => item.id) : value?.id ?? ''}
          onChange={handleChange}
          onBlur={(e) => {
            if (onBlur) {
              onBlur(e);
            }
          }}
          native={false}
          disabled={disabled}
          multiple={multiple}
          sx={{
            position: 'relative',
            ...sx
          }}
          data-selected={hasValue}
          data-rounded={rounded}
          className={clsx(
            style.select,
            rounded && style.rounded,
            disabled && style.disabled,
            rounded && disabled && style.roundedDisabled,
            size === 'small' && style.small,
            size === 'medium' && style.medium,
            size === 'normal' && style.normal,
            type === 'success' && style.success,
            type === 'warning' && style.warning,
            withGradient && style.gradient,
            className
          )}
          displayEmpty
          renderValue={(selected) => {
            if (Array.isArray(value) && hasValue) {
              return <span>{value.map((item) => item.name).join(', ')}</span>;
            } else if (!Array.isArray(value) && hasValue) {
              return <span>{value?.name}</span>;
            }
            return <span>{label}</span>;
          }}
          inputProps={{ 'aria-label': 'Without label' }}
          MenuProps={MenuProps}
          data-test-id="dropdown-list"
        >
          <ListSubheader
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            sx={{
              padding: '0px !important'
            }}
          >
            <InputField
              classnamesProps={style.search}
              onBlur={onInputBlur}
              onChange={handleSearchInputChange}
              value={searchInputValue}
              tags={<span className="material-icons">search</span>}
              placeholder="Search all insurance plans"
              endAdornment={
                searchInputValue ? (
                  <span
                    className={clsx('material-icons', style.closeIcon)}
                    onClick={() =>
                      onSearchInputChange?.({
                        target: { value: '' }
                      } as ChangeEvent<HTMLInputElement>)
                    }
                  >
                    highlight_off
                  </span>
                ) : null
              }
              onEndAdornmentClick={() => {
                onSearchInputChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
              }}
            />
          </ListSubheader>
          {Array.isArray(value) && value.length > 0 && (
            <div
              className={style.tags}
              data-test-id="selected-tags"
              style={{ width: selectWidth ? selectWidth + fitWidthMore : 'min-content' }}
            >
              {value.map((v) => renderTag({ id: v.id, name: v.name }))}
            </div>
          )}
          {categorizedItems.map((item) => {
            if (item.children) {
              const items = [item, ...item.children];
              return items.map((listItem) => {
                if (!categorySelectable && 'children' in listItem) {
                  return (
                    <ListSubheader key={listItem.id} disableSticky>
                      {listItem.name}
                    </ListSubheader>
                  );
                }
                return renderMenuitem(listItem);
              });
            }
            return renderMenuitem(item);
          })}
        </Select>
      </FormControl>
    </div>
  );
};

export default InsurancePlanSelect;
